import { PORTAL_API_BASE_URL } from '../../../constants';
import request from '../../../util/APIUtils';

const BASE = `${PORTAL_API_BASE_URL}/api/content/location-config`;

const req = (opts) => request({ ...opts, throw400Error: true });

/**
 * Fetch location tree (nested by parent).
 * @returns {Promise<Array<{ id: string, name: string, level: number, children: Array }>>}
 */
export function getLocations() {
  return req({ url: `${BASE}/locations`, method: 'GET' });
}

/**
 * Fetch resolved config (services + resources, direct + inherited) for a location.
 * @param {string} locationId - Location short_code
 * @returns {Promise<{ services: Array, resources: Array }>}
 */
export function getLocationConfig(locationId) {
  return req({ url: `${BASE}/locations/${encodeURIComponent(locationId)}/config`, method: 'GET' });
}

/**
 * Fetch all services (reference list).
 * @returns {Promise<Array<{ id: number, name: string }>>}
 */
export function getServices() {
  return req({ url: `${BASE}/services`, method: 'GET' });
}

/**
 * Fetch all resources (reference list).
 * @returns {Promise<Array<{ id: number, name: string, description: string|null }>>}
 */
export function getResources() {
  return req({ url: `${BASE}/resources`, method: 'GET' });
}

/**
 * Create service–location association.
 * @param {string} locationId
 * @param {object} body - { service_key, availability_type?, available?, auto_provision_capable?, sso_enabled?, assignable?, external_id?, location_spcfc_url?, external_group?, automated_provisioning_assn?, explorable? }
 */
export function createServiceAssociation(locationId, body) {
  return req({
    url: `${BASE}/locations/${encodeURIComponent(locationId)}/service-associations`,
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Update service–location association.
 * @param {string} locationId
 * @param {string|number} associationId
 * @param {object} body - { availability_type?, available?, auto_provision_capable?, sso_enabled?, assignable?, external_id?, location_spcfc_url?, external_group?, automated_provisioning_assn?, explorable? }
 */
export function updateServiceAssociation(locationId, associationId, body) {
  return req({
    url: `${BASE}/locations/${encodeURIComponent(locationId)}/service-associations/${associationId}`,
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Delete service–location association.
 * @param {string} locationId
 * @param {string|number} associationId
 */
export function deleteServiceAssociation(locationId, associationId) {
  return req({
    url: `${BASE}/locations/${encodeURIComponent(locationId)}/service-associations/${associationId}`,
    method: 'DELETE',
  });
}

/**
 * Create location–resource association.
 * @param {string} locationId
 * @param {object} body - { resource_key, active?, record_status?, attributes? }
 */
export function createResourceAssociation(locationId, body) {
  return req({
    url: `${BASE}/locations/${encodeURIComponent(locationId)}/resource-associations`,
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Update location–resource association.
 * @param {string} locationId
 * @param {string|number} associationId
 * @param {object} body
 */
export function updateResourceAssociation(locationId, associationId, body) {
  return req({
    url: `${BASE}/locations/${encodeURIComponent(locationId)}/resource-associations/${associationId}`,
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Delete location–resource association.
 * @param {string} locationId
 * @param {string|number} associationId
 */
export function deleteResourceAssociation(locationId, associationId) {
  return req({
    url: `${BASE}/locations/${encodeURIComponent(locationId)}/resource-associations/${associationId}`,
    method: 'DELETE',
  });
}

/**
 * Fetch all templates.
 * @returns {Promise<Array<{ id: string, name: string, description: string|null, updated: string, services: Array, resources: Array }>>}
 */
export function getTemplates() {
  return req({ url: `${BASE}/templates`, method: 'GET' });
}

/**
 * Fetch one template by id.
 * @param {string} id - UUID
 */
export function getTemplate(id) {
  return req({ url: `${BASE}/templates/${encodeURIComponent(id)}`, method: 'GET' });
}

/**
 * Create template.
 * @param {object} body - { name, description?, services: Array, resources: Array }
 */
export function createTemplate(body) {
  return req({
    url: `${BASE}/templates`,
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Update template.
 * @param {string} id - UUID
 * @param {object} body - { name?, description?, services?, resources? }
 */
export function updateTemplate(id, body) {
  return req({
    url: `${BASE}/templates/${encodeURIComponent(id)}`,
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Delete template.
 * @param {string} id - UUID
 */
export function deleteTemplate(id) {
  return req({
    url: `${BASE}/templates/${encodeURIComponent(id)}`,
    method: 'DELETE',
  });
}

/**
 * Apply template to multiple locations (bulk).
 * @param {string} templateId - UUID
 * @param {{ locationIds: string[], mode: 'merge' | 'overwrite' }} body
 * @returns {Promise<{ applied: number, errors?: Array<{ locationId: string, message: string }> }>}
 */
export function applyTemplate(templateId, body) {
  return req({
    url: `${BASE}/templates/${encodeURIComponent(templateId)}/apply`,
    method: 'POST',
    body: JSON.stringify(body),
  });
}
